import { useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import * as ContextMenu from "@radix-ui/react-context-menu";
import {
  Archive,
  ChevronRight,
  Copy,
  CopyPlus,
  Download,
  FileText,
  Folder,
  FolderOpen,
  History,
  Lock,
  LogOut,
  Pencil,
  Plus,
  Settings2,
  Star,
  Trash2,
  FilePlus2,
  FolderPlus,
  Users,
} from "lucide-react";
import type { WorkspaceItem } from "@hydra-notes/shared";
import { ctxFor, getData, useAllSpaceItems } from "@/data/store";
import { useSharedFolders, sharedAccent, sharedFoldersApi, type SharedFolderMeta } from "@/data/sharedFolders";
import { useAuth } from "@/data/auth";
import { useExpanded } from "@/store/expanded";
import { useTabs } from "@/store/tabs";
import { useRenameRequest } from "@/store/renameRequest";
import { useHistoryDialog } from "@/components/HistoryDialog";
import { useSharedFolderDialog } from "@/components/SharedFolderDialog";
import { useCreateSharedFolderDialog } from "@/components/CreateSharedFolderDialog";
import { useTransferDialog } from "@/components/TransferConfirmDialog";
import { openItem, createNote, createFolder, trashItem, archiveItem, duplicateNote, roleIn } from "@/commands/nav";
import { exportNote, exportZip } from "@/data/export";
import { toast } from "@/ui/toast";
import s from "./Tree.module.css";

type HeaderKind = "plain" | "shared-root" | "shared-folder";

interface FlatRow {
  item: WorkspaceItem;
  /** Space this row belongs to ("" for headers with no space). */
  spaceId: string;
  shared: boolean;
  /** css color value for shared rows (from the folder's palette key) */
  accent?: string;
  depth: number;
  isFolder: boolean;
  expanded: boolean;
  hasChildren: boolean;
  /** rendered in the Favorites section (flat, starred) */
  favorite?: boolean;
  /** section / shared-folder header row */
  header?: { label: string; kind: HeaderKind; folder?: SharedFolderMeta };
}

type DropPos = "into" | "above" | "below";

interface DragPayload {
  id: string;
  spaceId: string;
}

function readDragPayload(e: React.DragEvent): DragPayload | null {
  const raw = e.dataTransfer.getData("application/x-hydra-item");
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as DragPayload;
    return typeof parsed.id === "string" ? parsed : null;
  } catch {
    // pre-JSON payloads (older tab) carried the bare id; treat as personal
    return { id: raw, spaceId: getData().workspaceId };
  }
}

/** Expanded-state key of a shared folder's header row. */
const sfKey = (folderId: string) => `sf:${folderId}`;

export function Tree() {
  const spaces = useAllSpaceItems();
  const folders = useSharedFolders((st) => st.folders);
  const signedIn = useAuth((st) => st.status === "signedIn");
  const { expanded, toggle } = useExpanded();
  const activeItemId = useTabs((st) => {
    const g = st.groups.find((g) => g.id === st.activeGroupId);
    return g?.tabs.find((t) => t.id === g.activeTabId)?.itemId ?? null;
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [drop, setDrop] = useState<{ id: string; pos: DropPos } | null>(null);

  // external rename requests (e.g. the New Folder header button)
  const renameReq = useRenameRequest();
  const lastNonce = useRef(0);
  if (renameReq.id && renameReq.nonce !== lastNonce.current) {
    lastNonce.current = renameReq.nonce;
    if (renamingId !== renameReq.id) setRenamingId(renameReq.id);
  }

  const personalSpaceId = spaces[0]?.spaceId ?? "";

  const rows = useMemo(() => {
    const out: FlatRow[] = [];
    const headerStub = (id: string, label: string, kind: HeaderKind, folder?: SharedFolderMeta): FlatRow => ({
      item: { id } as WorkspaceItem,
      spaceId: folder?.id ?? "",
      shared: kind !== "plain",
      ...(folder ? { accent: sharedAccent(folder.colorKey) } : {}),
      depth: 0,
      isFolder: false,
      expanded: false,
      hasChildren: false,
      header: { label, kind, ...(folder ? { folder } : {}) },
    });

    const walkSpace = (spaceItems: WorkspaceItem[], spaceId: string, shared: boolean, accent: string | undefined, baseDepth: number) => {
      // Task lists and Cards each have a dedicated app surface.
      const live = spaceItems.filter(
        (i) => i.type !== "card" && i.type !== "cardboard" && i.type !== "tasklist" && !i.trashedAt && !i.archivedAt,
      );
      const byParent = new Map<string | null, WorkspaceItem[]>();
      for (const item of live) {
        const list = byParent.get(item.parentId) ?? [];
        list.push(item);
        byParent.set(item.parentId, list);
      }
      const walk = (parentId: string | null, depth: number) => {
        for (const item of byParent.get(parentId) ?? []) {
          const isFolder = item.type === "folder";
          const kids = byParent.get(item.id) ?? [];
          const isExpanded = isFolder && !!expanded[item.id];
          out.push({
            item,
            spaceId,
            shared,
            ...(accent ? { accent } : {}),
            depth,
            isFolder,
            expanded: isExpanded,
            hasChildren: kids.length > 0,
          });
          if (isExpanded) walk(item.id, depth + 1);
        }
      };
      walk(null, baseDepth);
      return live;
    };

    // ---- personal space (favorites + tree), exactly as before ----
    const personal = spaces[0];
    if (personal) {
      const live = personal.items.filter(
        (i) => i.type !== "card" && i.type !== "cardboard" && i.type !== "tasklist" && !i.trashedAt && !i.archivedAt,
      );
      const pinned = live.filter((i) => i.pinned).sort((a, b) => (a.title || "").localeCompare(b.title || ""));
      if (pinned.length > 0) {
        out.push(headerStub("__hdr_Favorites", "Favorites", "plain"));
        for (const item of pinned) {
          out.push({
            item,
            spaceId: personal.spaceId,
            shared: false,
            depth: 0,
            isFolder: item.type === "folder",
            expanded: false,
            hasChildren: false,
            favorite: true,
          });
        }
        out.push(headerStub("__hdr_Notes", "Notes", "plain"));
      }
      walkSpace(personal.items, personal.spaceId, false, undefined, 0);
    }

    // ---- shared folders section (always shown signed-in, so the + button
    //      makes the feature discoverable even with zero folders) ----
    if (signedIn) {
      out.push(headerStub("__hdr_Shared", "Shared", "shared-root"));
      const sorted = [...folders].sort((a, b) => a.title.localeCompare(b.title));
      for (const folder of sorted) {
        out.push(headerStub(sfKey(folder.id), folder.title, "shared-folder", folder));
        if (folder.locked) continue;
        if (!expanded[sfKey(folder.id)]) continue;
        const space = spaces.find((sp) => sp.spaceId === folder.id);
        if (space) walkSpace(space.items, folder.id, true, sharedAccent(folder.colorKey), 1);
      }
    }
    return out;
  }, [spaces, folders, expanded, signedIn]);

  const scrollerRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollerRef.current,
    estimateSize: () => 24,
    getItemKey: (i) => {
      const r = rows[i]!;
      return r.header ? r.item.id : r.favorite ? `fav_${r.item.id}` : r.item.id;
    },
    overscan: 12,
  });

  const commitRename = (id: string, title: string, spaceId: string) => {
    setRenamingId(null);
    const ctx = ctxFor(spaceId) ?? getData();
    const item = ctx.workspace.getItem(id);
    if (item && title !== item.title) ctx.workspace.rename(id, title);
  };

  /** Move within one space, or open the transfer confirm across spaces. */
  const handleDrop = (e: React.DragEvent, target: FlatRow, pos: DropPos) => {
    e.preventDefault();
    setDrop(null);
    const payload = readDragPayload(e);
    if (!payload || payload.id === target.item.id) return;

    // dropping onto a shared folder's header row = into its root
    const isFolderHeader = target.header?.kind === "shared-folder";
    const targetSpaceId = isFolderHeader ? target.header!.folder!.id : target.spaceId;

    if (payload.spaceId !== targetSpaceId) {
      const sourceCtx = ctxFor(payload.spaceId);
      const targetCtx = ctxFor(targetSpaceId);
      if (!sourceCtx || !targetCtx) {
        toast.error("That folder is not available right now.");
        return;
      }
      if (roleIn(sourceCtx) !== "owner" && sourceCtx.kind === "shared") {
        toast.error("Only the folder owner can move notes out of a shared folder.");
        return;
      }
      const targetRole = roleIn(targetCtx);
      if (targetCtx.kind === "shared" && targetRole !== "owner" && targetRole !== "editor") {
        toast.error("You have view-only access to that folder.");
        return;
      }
      const targetParentId = isFolderHeader
        ? null
        : pos === "into" && target.isFolder
          ? target.item.id
          : target.item.parentId;
      useTransferDialog.getState().open({
        itemId: payload.id,
        sourceSpaceId: payload.spaceId,
        targetSpaceId,
        targetParentId,
      });
      return;
    }

    const ws = (ctxFor(targetSpaceId) ?? getData()).workspace;
    const dragged = ws.getItem(payload.id);
    if (!dragged) return;
    const ctx = ctxFor(targetSpaceId) ?? getData();
    if (ctx.kind === "shared" && !["owner", "editor"].includes(roleIn(ctx))) {
      toast.error("You have view-only access to this folder.");
      return;
    }
    const moveWithinSpace = (parentId: string | null, beforeId?: string | null) => {
      const changedParent = dragged.parentId !== parentId;
      ws.move(payload.id, parentId, beforeId);
      if (!changedParent) return;
      const destination = parentId
        ? ws.getItem(parentId)?.title || "Untitled folder"
        : ctx.kind === "shared"
          ? folders.find((folder) => folder.id === ctx.workspaceId)?.title || "Shared notes"
          : "Notes";
      toast.success(`Moved "${dragged.title || "Untitled"}" to "${destination}"`);
    };
    if (isFolderHeader) {
      moveWithinSpace(null);
      return;
    }
    if (pos === "into" && target.isFolder) {
      moveWithinSpace(target.item.id);
      useExpanded.getState().expand(target.item.id);
    } else if (pos === "above") {
      moveWithinSpace(target.item.parentId, target.item.id);
    } else if (pos === "below") {
      const siblings = ws.childrenOf(target.item.parentId);
      const idx = siblings.findIndex((x) => x.id === target.item.id);
      const next = siblings[idx + 1];
      moveWithinSpace(target.item.parentId, next?.id ?? undefined);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (renamingId) return;
    const idx = rows.findIndex((r) => r.item.id === selectedId);
    const select = (from: number, dir: 1 | -1) => {
      let i = Math.max(0, Math.min(rows.length - 1, from));
      // plain section headers are not selectable; folder headers are
      while (rows[i] && rows[i]!.header && rows[i]!.header!.kind !== "shared-folder") {
        i += dir;
        if (i < 0 || i >= rows.length) return;
      }
      const row = rows[i];
      if (row) {
        setSelectedId(row.item.id);
        virtualizer.scrollToIndex(i);
      }
    };
    const selected = rows[idx];
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        select(idx + 1, 1);
        break;
      case "ArrowUp":
        e.preventDefault();
        select(idx - 1, -1);
        break;
      case "ArrowRight": {
        if (selected?.header?.kind === "shared-folder") {
          if (!expanded[selected.item.id] && !selected.header.folder?.locked) toggle(selected.item.id);
          else select(idx + 1, 1);
        } else if (selected?.isFolder && !selected.expanded) toggle(selected.item.id);
        else select(idx + 1, 1);
        e.preventDefault();
        break;
      }
      case "ArrowLeft": {
        if (selected?.header?.kind === "shared-folder") {
          if (expanded[selected.item.id]) toggle(selected.item.id);
        } else if (selected?.isFolder && selected.expanded) {
          toggle(selected.item.id);
        } else if (selected) {
          const parentIdx = rows.findIndex((r) => r.item.id === selected.item.parentId);
          if (parentIdx >= 0) select(parentIdx, -1);
        }
        e.preventDefault();
        break;
      }
      case "Enter":
        if (selected?.header?.kind === "shared-folder") {
          if (!selected.header.folder?.locked) toggle(selected.item.id);
        } else if (selectedId) openItem(selectedId);
        e.preventDefault();
        break;
      case "F2":
        if (selectedId && !selected?.header) setRenamingId(selectedId);
        e.preventDefault();
        break;
      case "Delete":
        if (selectedId && !selected?.header) trashItem(selectedId);
        e.preventDefault();
        break;
    }
  };

  return (
    <div ref={scrollerRef} className={s.scroller} tabIndex={0} role="tree" onKeyDown={onKeyDown}>
      <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
        {virtualizer.getVirtualItems().map((v) => {
          const row = rows[v.index]!;
          const { item } = row;
          const rowTop: React.CSSProperties = {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            transform: `translateY(${v.start}px)`,
          };
          if (row.header?.kind === "plain") {
            return (
              <div key={item.id} className={s.sectionHeader} style={rowTop}>
                {row.header.label}
              </div>
            );
          }
          if (row.header?.kind === "shared-root") {
            return (
              <div key={item.id} className={s.sectionHeader} style={rowTop}>
                <span className={s.sectionHeaderLabel}>{row.header.label}</span>
                <button
                  type="button"
                  className={s.sectionAction}
                  title="New shared folder"
                  onClick={(e) => {
                    e.stopPropagation();
                    useCreateSharedFolderDialog.getState().setOpen(true);
                  }}
                >
                  <Plus size={12} />
                </button>
              </div>
            );
          }
          if (row.header?.kind === "shared-folder") {
            const folder = row.header.folder!;
            const isExpanded = !!expanded[item.id] && !folder.locked;
            return <SharedFolderHeader key={item.id} row={row} folder={folder} isExpanded={isExpanded} style={rowTop} selected={selectedId === item.id} onSelect={() => setSelectedId(item.id)} onToggle={() => !folder.locked && toggle(item.id)} dropState={drop?.id === item.id ? drop.pos : null} onDragOver={(e) => {
              if (folder.locked) return;
              e.preventDefault();
              setDrop({ id: item.id, pos: "into" });
            }} onDragLeave={() => setDrop((d) => (d?.id === item.id ? null : d))} onDrop={(e) => handleDrop(e, row, "into")} />;
          }
          const isActive = item.id === activeItemId;
          const dropState = drop?.id === item.id ? drop.pos : null;
          const role = row.shared ? (folders.find((f) => f.id === row.spaceId)?.role ?? "viewer") : "owner";
          const mayEdit = role === "owner" || role === "editor";
          return (
            <ContextMenu.Root key={row.favorite ? `fav_${item.id}` : item.id}>
              <ContextMenu.Trigger asChild>
                <div
                  role="treeitem"
                  aria-expanded={row.isFolder ? row.expanded : undefined}
                  className={[
                    s.row,
                    row.shared ? s.rowShared : "",
                    isActive ? s.rowActive : "",
                    selectedId === item.id ? s.rowSelected : "",
                    dropState === "into" ? s.dropInto : "",
                    dropState === "above" ? s.dropAbove : "",
                    dropState === "below" ? s.dropBelow : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  style={{
                    ...rowTop,
                    paddingLeft: 4 + row.depth * 14,
                    ...(row.accent ? ({ "--sf-accent": row.accent } as React.CSSProperties) : {}),
                  }}
                  draggable={renamingId !== item.id}
                  onDragStart={(e) => {
                    e.dataTransfer.setData(
                      "application/x-hydra-item",
                      JSON.stringify({ id: item.id, spaceId: row.spaceId } satisfies DragPayload),
                    );
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    const rect = e.currentTarget.getBoundingClientRect();
                    const y = e.clientY - rect.top;
                    const pos: DropPos = row.isFolder
                      ? y < rect.height * 0.25
                        ? "above"
                        : y > rect.height * 0.75
                          ? "below"
                          : "into"
                      : y < rect.height / 2
                        ? "above"
                        : "below";
                    setDrop({ id: item.id, pos });
                  }}
                  onDragLeave={() => setDrop((d) => (d?.id === item.id ? null : d))}
                  onDrop={(e) => dropState && handleDrop(e, row, dropState)}
                  onClick={() => {
                    setSelectedId(item.id);
                    openItem(item.id);
                  }}
                  onDoubleClick={() => mayEdit && setRenamingId(item.id)}
                >
                  {row.isFolder ? (
                    <span
                      className={[s.chevron, row.expanded ? s.chevronOpen : ""].join(" ")}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggle(item.id);
                      }}
                    >
                      <ChevronRight size={13} />
                    </span>
                  ) : (
                    <span className={s.chevronSpacer} />
                  )}
                  <span className={s.icon}>
                    {row.favorite ? (
                      <Star size={13} className={s.starIcon} />
                    ) : row.isFolder ? (
                      row.expanded ? (
                        <FolderOpen size={14} />
                      ) : (
                        <Folder size={14} />
                      )
                    ) : (
                      <FileText size={14} />
                    )}
                  </span>
                  {renamingId === item.id ? (
                    <input
                      className={s.renameInput}
                      defaultValue={item.title}
                      autoFocus
                      onFocus={(e) => e.target.select()}
                      onClick={(e) => e.stopPropagation()}
                      onBlur={(e) => commitRename(item.id, e.target.value, row.spaceId)}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === "Enter") commitRename(item.id, (e.target as HTMLInputElement).value, row.spaceId);
                        if (e.key === "Escape") setRenamingId(null);
                      }}
                    />
                  ) : (
                    <span className={[s.label, item.title ? "" : s.untitled].join(" ")}>
                      {item.title || "Untitled"}
                    </span>
                  )}
                </div>
              </ContextMenu.Trigger>
              <ContextMenu.Portal>
                <ContextMenu.Content className="hn-menu">
                  {row.isFolder && mayEdit ? (
                    <>
                      <ContextMenu.Item className="hn-menu-item" onSelect={() => createNote(item.id)}>
                        <FilePlus2 /> New note inside
                      </ContextMenu.Item>
                      <ContextMenu.Item
                        className="hn-menu-item"
                        onSelect={() => {
                          const id = createFolder(item.id);
                          useExpanded.getState().expand(item.id);
                          if (id) setRenamingId(id);
                        }}
                      >
                        <FolderPlus /> New folder inside
                      </ContextMenu.Item>
                      <ContextMenu.Separator className="hn-menu-sep" />
                    </>
                  ) : null}
                  {mayEdit ? (
                    <ContextMenu.Item className="hn-menu-item" onSelect={() => setRenamingId(item.id)}>
                      <Pencil /> Rename
                    </ContextMenu.Item>
                  ) : null}
                  {!row.shared ? (
                    <ContextMenu.Item
                      className="hn-menu-item"
                      onSelect={() => getData().workspace.setPinned(item.id, !item.pinned)}
                    >
                      <Star /> {item.pinned ? "Unpin from favorites" : "Pin to favorites"}
                    </ContextMenu.Item>
                  ) : null}
                  {item.type === "note" ? (
                    <>
                      {mayEdit ? (
                        <ContextMenu.Item className="hn-menu-item" onSelect={() => void duplicateNote(item.id)}>
                          <CopyPlus /> Duplicate
                        </ContextMenu.Item>
                      ) : null}
                      <ContextMenu.Item
                        className="hn-menu-item"
                        onSelect={() => {
                          void navigator.clipboard.writeText(`[[${item.title || "Untitled"}]]`);
                        }}
                      >
                        <Copy /> Copy wikilink
                      </ContextMenu.Item>
                      <ContextMenu.Item className="hn-menu-item" onSelect={() => useHistoryDialog.getState().open(item.id)}>
                        <History /> Version history
                      </ContextMenu.Item>
                    </>
                  ) : null}
                  <ContextMenu.Item
                    className="hn-menu-item"
                    onSelect={() => (row.isFolder ? void exportZip(item.id) : void exportNote(item.id))}
                  >
                    <Download /> Export {row.isFolder ? "as zip" : "markdown"}
                  </ContextMenu.Item>
                  {item.builtin === "tasks" ? null : !row.shared ? (
                    <>
                      <ContextMenu.Separator className="hn-menu-sep" />
                      <ContextMenu.Item className="hn-menu-item" onSelect={() => archiveItem(item.id)}>
                        <Archive /> Archive
                      </ContextMenu.Item>
                      <ContextMenu.Item className="hn-menu-item danger" onSelect={() => trashItem(item.id)}>
                        <Trash2 /> Move to trash
                      </ContextMenu.Item>
                    </>
                  ) : role === "owner" ? (
                    <>
                      <ContextMenu.Separator className="hn-menu-sep" />
                      <ContextMenu.Item className="hn-menu-item danger" onSelect={() => trashItem(item.id)}>
                        <Trash2 /> Move to trash
                      </ContextMenu.Item>
                    </>
                  ) : null}
                </ContextMenu.Content>
              </ContextMenu.Portal>
            </ContextMenu.Root>
          );
        })}
      </div>
    </div>
  );
}

function SharedFolderHeader(props: {
  row: FlatRow;
  folder: SharedFolderMeta;
  isExpanded: boolean;
  style: React.CSSProperties;
  selected: boolean;
  dropState: DropPos | null;
  onSelect: () => void;
  onToggle: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
}) {
  const { folder, isExpanded } = props;
  const isOwner = folder.role === "owner";
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>
        <div
          role="treeitem"
          aria-expanded={isExpanded}
          className={[
            s.row,
            s.sfHeader,
            folder.locked ? s.sfLocked : "",
            props.selected ? s.rowSelected : "",
            props.dropState === "into" ? s.dropInto : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={{ ...props.style, "--sf-accent": sharedAccent(folder.colorKey) } as React.CSSProperties}
          onClick={() => {
            props.onSelect();
            props.onToggle();
          }}
          onDragOver={props.onDragOver}
          onDragLeave={props.onDragLeave}
          onDrop={props.onDrop}
        >
          <span className={[s.chevron, isExpanded ? s.chevronOpen : ""].join(" ")}>
            {folder.locked ? <Lock size={12} /> : <ChevronRight size={13} />}
          </span>
          <span className={s.sfDot} />
          <span className={[s.label, s.sfLabel].join(" ")}>{folder.title}</span>
          {folder.locked ? (
            <span className={s.sfBadge}>over quota</span>
          ) : folder.role !== "owner" ? (
            <span className={s.sfBadge}>{folder.role}</span>
          ) : null}
        </div>
      </ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content className="hn-menu">
          {!folder.locked && (isOwner || folder.role === "editor") ? (
            <ContextMenu.Item
              className="hn-menu-item"
              onSelect={() => {
                createNoteInShared(folder.id);
              }}
            >
              <FilePlus2 /> New note inside
            </ContextMenu.Item>
          ) : null}
          <ContextMenu.Item className="hn-menu-item" onSelect={() => useSharedFolderDialog.getState().open(folder.id)}>
            <Settings2 /> Folder settings
          </ContextMenu.Item>
          {isOwner ? (
            <ContextMenu.Item className="hn-menu-item" onSelect={() => useSharedFolderDialog.getState().open(folder.id, "members")}>
              <Users /> Members and invites
            </ContextMenu.Item>
          ) : (
            <ContextMenu.Item
              className="hn-menu-item danger"
              onSelect={() => {
                void sharedFoldersApi
                  .leave(folder.id)
                  .then(() => useSharedFolders.getState().refresh())
                  .then(() => toast.info(`You left "${folder.title}".`))
                  .catch((err: Error) => toast.error(err.message));
              }}
            >
              <LogOut /> Leave folder
            </ContextMenu.Item>
          )}
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}

function createNoteInShared(folderId: string): void {
  const ctx = ctxFor(folderId);
  if (!ctx) return;
  const id = ctx.workspace.createItem("note", null, "");
  useExpanded.getState().expand(sfKey(folderId));
  openItem(id, { newTab: true });
}
